import { TESTER_BASE_URL, type SharedHandler } from "./http";

export type GetObject = (name: string) => Promise<Response>;

interface RequestItem {
  seq: number;
  route?: string;
}
interface LogLine {
  n: number;
  text: string;
}
interface LogDoc {
  source: string;
  partial: boolean;
  lines: LogLine[];
}

const MEDIA = "content-type";

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), { status, headers: { [MEDIA]: "application/json" } });
}

function failure(status: number, code: string, message: string): Response {
  return json({ error: { code, message, request_id: "" } }, status);
}

const missing = (what: string) => failure(404, "NOT_FOUND", `${what} was not shared with this run.`);
const readOnly = () => failure(403, "FORBIDDEN", "A shared run is read-only.");

function intParam(q: URLSearchParams, name: string, def: number, max: number): number {
  const n = Number.parseInt(q.get(name) ?? "", 10);
  return Number.isFinite(n) && n >= 0 ? Math.min(n, max) : def;
}

export function pageRequests(items: RequestItem[], q: URLSearchParams) {
  const route = q.get("route") ?? "";
  const after = Math.max(intParam(q, "after", 0, Number.MAX_SAFE_INTEGER), intParam(q, "cursor", 0, Number.MAX_SAFE_INTEGER));
  const limit = Math.max(1, intParam(q, "limit", 100, 500));
  const matched = items.filter((r) => r.seq > after && (route === "" || r.route === route)).sort((a, b) => a.seq - b.seq);
  const page = matched.slice(0, limit);
  const out: { items: RequestItem[]; next_cursor?: string } = { items: page };
  if (matched.length > limit && page.length > 0) out.next_cursor = String(page[page.length - 1]!.seq);
  return out;
}

export function pageLogs(doc: LogDoc, q: URLSearchParams) {
  const needle = (q.get("q") ?? "").toLowerCase();
  const tail = intParam(q, "tail", 0, 10_000);
  const offset = intParam(q, "offset", 0, 1 << 30);
  const limit = Math.max(1, intParam(q, "limit", 1000, 5000));
  let lines = needle ? doc.lines.filter((l) => l.text.toLowerCase().includes(needle)) : doc.lines;
  const out: { source: string; partial: boolean; lines: LogLine[]; next_offset?: number } = {
    source: doc.source,
    partial: doc.partial,
    lines: [],
  };
  if (tail > 0) {
    out.lines = lines.length > tail ? lines.slice(lines.length - tail) : lines;
    return out;
  }
  lines = lines.filter((l) => l.n > offset);
  if (lines.length > limit) out.next_offset = lines[limit - 1]!.n;
  out.lines = lines.slice(0, limit);
  return out;
}

export function pageEvents(items: Array<{ event_id: string }>, q: URLSearchParams) {
  const after = q.get("after");
  const limit = Math.max(1, intParam(q, "limit", 1000, 1000));
  const start = after ? items.findIndex((e) => e.event_id === after) + 1 : 0;
  return { items: items.slice(start, start + limit) };
}

function sliceBytes(res: Response, bytes: ArrayBuffer, range: string | null): Response {
  const type = res.headers.get(MEDIA) ?? "application/octet-stream";
  const m = /^bytes=(\d*)-(\d*)$/.exec(range ?? "");
  if (!m || (m[1] === "" && m[2] === "")) return new Response(bytes, { status: 200, headers: { [MEDIA]: type, "accept-ranges": "bytes" } });
  const size = bytes.byteLength;
  const start = m[1] === "" ? Math.max(0, size - Number(m[2])) : Number(m[1]);
  const end = m[1] === "" || m[2] === "" ? size - 1 : Math.min(Number(m[2]), size - 1);
  if (start > end || start >= size) return new Response(null, { status: 416, headers: { "content-range": `bytes */${size}` } });
  return new Response(bytes.slice(start, end + 1), {
    status: 206,
    headers: { [MEDIA]: type, "accept-ranges": "bytes", "content-range": `bytes ${start}-${end}/${size}` },
  });
}

const REPORT_OBJECT: Record<string, string> = { json: "report.json", html: "report.html", md: "report.md" };

export function createSharedHandler(runId: string, getObject: GetObject): SharedHandler {
  const prefix = new URL(TESTER_BASE_URL, globalThis.location?.href ?? "http://localhost/").pathname.replace(/\/$/, "");

  const object = async <T>(name: string, what: string): Promise<{ data: T } | { error: Response }> => {
    const res = await getObject(name);
    if (res.status === 404) return { error: missing(what) };
    if (!res.ok) return { error: res };
    return { data: (await res.json()) as T };
  };
  const raw = async (name: string, what: string): Promise<Response> => {
    const res = await getObject(name);
    return res.status === 404 ? missing(what) : res;
  };

  return async (req) => {
    const url = new URL(req.url);
    if (!url.pathname.startsWith(prefix + "/")) return null;
    const path = url.pathname.slice(prefix.length);
    const parts = path.split("/").filter(Boolean).map(decodeURIComponent);
    const q = url.searchParams;
    const [head, id, tail, sub] = parts;
    const forRun = head === "runs" && id === runId;
    if (!forRun && head !== "executions" && head !== "evidence" && !(head === "catalog" && id === "tests")) return null;
    if (req.method !== "GET") return readOnly();

    if (head === "catalog") {
      return tail === undefined ? raw("catalog/tests.json", "The test list") : raw(`catalog/test/${tail}.json`, "This test");
    }

    if (forRun) {
      if (tail === undefined) return raw("run.json", "This run");
      if (tail === "events") {
        const r = await object<{ items: Array<{ event_id: string }> }>("events.json", "The event log");
        return "error" in r ? r.error : json(pageEvents(r.data.items, q));
      }
      if (tail === "report") {
        const name = REPORT_OBJECT[q.get("format") ?? "json"];
        return name ? raw(name, "The report") : failure(400, "INVALID_REQUEST", "format must be json, html or md");
      }
      return failure(404, "NOT_FOUND", "This is not available on a shared run.");
    }

    if (head === "evidence") {
      if (tail !== "content" || !id) return failure(404, "NOT_FOUND", "No such endpoint");
      const res = await raw(`evidence/${id}`, "This evidence");
      if (!res.ok) return res;
      return sliceBytes(res, await res.arrayBuffer(), req.headers.get("range"));
    }

    if (!id) return failure(404, "NOT_FOUND", "No such endpoint");
    const base = `exec/${id}`;
    if (tail === undefined) return raw(`${base}.json`, "This execution");
    if (tail === "bug-report") return raw(`${base}/bug-report.json`, "A bug report");
    if (tail === "requests" && sub === undefined) {
      const r = await object<{ items: RequestItem[] }>(`${base}/requests.json`, "The request list");
      return "error" in r ? r.error : json(pageRequests(r.data.items, q));
    }
    if (tail === "requests" && sub) {
      const r = await object<Record<string, unknown>>(`${base}/request-details.json`, "The request details");
      if ("error" in r) return r.error;
      return r.data[sub] === undefined ? missing("This request's detail") : json(r.data[sub]);
    }
    if (tail === "logs") {
      const source = q.get("source") ?? "agent";
      if (source !== "agent" && source !== "application") return failure(400, "INVALID_REQUEST", "source must be agent or application");
      const r = await object<LogDoc>(`${base}/logs-${source}.json`, "The log");
      return "error" in r ? r.error : json(pageLogs(r.data, q));
    }
    return failure(404, "NOT_FOUND", "This is not available on a shared run.");
  };
}
